import { useMemo } from "react"
import { Loader2 } from "lucide-react"

import { MESSENGERS } from "@/config/contacts"
import { Button as UiKitButton } from "@/components/ui-kit/button"
import { useContract } from "@/hooks/contracts/useContracts"
import { useFacilities } from "@/hooks/warehouse/useWarehouse"
import type { Facility } from "@/types/warehouse"

const RECEPTION_HOURS = "9:00 - 21:00"
const DEFAULT_RECEPTION_PHONE = "+78122070007"

const ON_SITE_STEPS = [
  {
    number: "01",
    text: "Оказавшись на складе PROSTOR введите пин-код на пин-паде для доступа в секцию боксов.",
  },
  {
    number: "02",
    text: "После успешного ввода откроются двери, снимется сигнализация с бокса и у вашего бокса загорится световая дорожка.",
  },
  {
    number: "03",
    text: "Внутри бокса на двери вы найдете индивидуальный механический замок. Используйте его, чтобы закрыть ваш бокс.",
  },
] as const

function getFacilityProperty(facility: Facility | undefined, code: string) {
  return facility?.properties?.find((property) => property.code === code)?.value
}

function StepsBlock() {
  const steps = ON_SITE_STEPS.map((step) => ({
    number: step.number,
    text: step.text,
  }))

  return (
    <div className="grid sm:min-h-[260px] sm:grid-cols-3 sm:grid-rows-[auto_1fr_auto] gap-2">
      {steps.map((step) => (
        <div
          key={step.number}
          className="row-span-3 grid grid-rows-subgrid gap-0 rounded-[8px] bg-background p-4"
        >
          <span className="inline-flex size-10 items-center justify-center rounded-[6px] bg-white text-[20px] leading-[24px] font-semibold text-brand">
            {step.number}
          </span>
          <div aria-hidden="true" className="min-h-0" />
          <p className="text-[17px] leading-[24px] text-foreground">{step.text}</p>  
        </div>
      ))}
    </div>
  )
}

export function PaymentDoneRightPanel({ contractId }: { contractId: number }) {
  const { data: contract, isLoading: isContractLoading } = useContract(contractId)
  const { data: facilities, isLoading: isFacilitiesLoading } = useFacilities()

  const facility = useMemo(() => {
    if (!contract || !facilities?.items) {
      return undefined
    }

    return facilities.items.find((item) => item.facility.code === contract.facility)?.facility
  }, [contract, facilities])

  // The facility's own chat where it has one, the company's otherwise.
  const chats = MESSENGERS.map((chat) => ({
    ...chat,
    href: getFacilityProperty(facility, `${chat.name.toLowerCase()}_link`) ?? chat.href,
  }))
  const receptionPhone =
    getFacilityProperty(facility, "reception_phone") ??
    getFacilityProperty(facility, "phone") ??
    DEFAULT_RECEPTION_PHONE

  const accessInstructionLink =
    getFacilityProperty(facility, "access_instruction_link") ??
    getFacilityProperty(facility, "access_link")

  const instructionQrImage =
    getFacilityProperty(facility, "access_instruction_qr") ??
    (accessInstructionLink
      ? `https://api.qrserver.com/v1/create-qr-code/?size=158x158&data=${encodeURIComponent(accessInstructionLink)}`
      : undefined)

  const isLoading = isContractLoading || isFacilitiesLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 md:h-full md:py-0">
        <Loader2 className="size-8 animate-spin text-brand" />
      </div>
    )
  }

  return (
    <div className="flex flex-col sm:p-8 px-2 py-6 md:h-full md:min-h-0 md:overflow-y-auto">
      <h2 className="mb-3 sm:text-[18px] text-[16px] leading-[20px] sm:leading-[24px] font-medium text-foreground">Что делать на месте?</h2>

      <div className="sm:mb-8 mb-6">
        <StepsBlock />
      </div>

      <div className="sm:mb-3 mb-4 flex flex-col sm:flex-row items-start justify-between sm:gap-4 gap-3">
        <h3 className="sm:text-[24px] text-[16px] leading-[20px] sm:leading-[28px] font-medium text-foreground">
          Если у вас остались вопросы позвоните или напишите нам
        </h3>
        <p className="shrink-0 pt-1 sm:text-lg text-[16px] leading-[20px] sm:leading-[24px] text-light-muted">
          Ресепшн ({RECEPTION_HOURS})
        </p>
      </div>

      <div className="sm:mb-8 mb-0 flex flex-col sm:flex-row gap-2">
        {chats.map(({ name, href, Icon }) => (
          <a
            key={name}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-w-0 sm:flex-1 h-[60px] items-center justify-center gap-[10px] rounded-lg bg-muted px-4 text-[18px] leading-[24px] text-foreground transition-colors hover:bg-white"
          >
            <Icon className="size-5" />
            Написать в {name}
          </a>
        ))}

        <UiKitButton
          type="button"
          variant="secondary"
          size="sm"
          className="min-w-0 sm:w-fit w-full h-[60px] text-[18px] leading-[24px] gap-[10px] bg-muted px-8"
          onClick={() => {
            window.location.href = `tel:${receptionPhone}`
          }}
        >
          Заказать звонок
        </UiKitButton>
      </div>

      <div className="flex flex-col gap-4 pt-6 md:mt-auto hidden sm:block">
        <p className="text-lg leading-6 text-light-muted">Открыть инструкцию<br />на телефоне</p>
        {instructionQrImage ? (
          <img
            src={instructionQrImage}
            alt=""
            width={158}
            height={158}
            className="size-[158px] shrink-0 bg-white object-contain"
          />
        ) : (
          <div className="size-[158px] shrink-0 bg-white" />
        )}
      </div>
    </div>
  )
}
